import { MutableRefObject } from 'react';

import { Location } from 'react-router-dom';

import * as FileSaver from 'file-saver';
import {
    ProcessCellForExportParams,
    ValueFormatterParams,
    RangeSelectionChangedEvent,
    IRowNode,
    GetRowIdParams,
    CellRange,
    SuppressKeyboardEventParams,
    GridApi,
    CellClassParams,
    RowDragEndEvent,
    Column,
    CellContextMenuEvent
} from '@ag-grid-community/core';
import { AgGridReact } from '@ag-grid-community/react';

import { isEmpty, isNil } from 'lodash-es';

import { getProxyFileUrl } from 'src/features/shared/utils/utils';
import { BodResponse, DatasheetTabDataResponse, hashAndVersionRequest, OmDataResponse, SelectedDatasheetData } from '../../queries/apiTypes';
import { Internationalization } from '@syncfusion/ej2-base';
import { RECOGNISED_EXCEL_FOMRATS, ROW_HEIGHT, ROW_LEVEL, SCHEDULE_FILE_TYPES, TAB_SUPPORTING_IMPORT_PRODUCTS, TabSupportImportProducts } from '../constants';
import { UploadedScheduleResponse, UploadedSubmittalPackageResponse } from '../../ImportMoreProducts/queries/apiTypes';
import { UploadedDoc } from '../../../Dashboard/BOMModal/queries/apiTypes';
import { MainProductComponentsDataType, MainProductDataType } from '../../pricing&LeadTime/mainProductTable';
import { ToolBarT } from '../../../shared/CustomToolBarPanel';
import { DEFAULT_VALUES_WARNING } from 'src/features/Dashboard/shared/constants';
import { DefaultValues, checkIfInDiscountFields } from 'src/features/Dashboard/shared/utils';
import { RoundingBehaviour } from '../types';
import { convertToDollar } from 'src/features/shared/utils/utils';
import { LotsResponse, PricingTableFromApis, PricingBodsResponse } from '../types';
import { NO_ROUNDING, BOM_TABLES_ENUM } from '../constants';
import { LotInfoType } from '../../pricing&LeadTime/mainProductTable';
import { CurrentDropNodeType } from '../../queries/apiTypes';
import { HierachyType } from '../hooks/useGetSelectedDataInfoHook';
import { CompanyMfgDetailsResponse } from 'src/features/Settings/Manufacturer/queries/apiTypes';
import { ColumnInfo } from '../types';

export const getTableData = <T extends Partial<OmDataResponse & DatasheetTabDataResponse>>(data1: BodResponse[] | undefined, data2?: T[] | undefined) => {
    if (data1?.length) {
        if (data2?.length) {
            const data2Map = new Map();
            data2.forEach((item) => {
                data2Map.set(item.bod_id, {
                    ...item,
                    id: item.bod_id,
                    drawing: item.selected_drawing,
                    installation_guide: item.selected_installation_guide,
                    other_document: item.selected_other_document,
                    warranty: item.selected_warranty,
                    datasheet: item.selected_datasheet
                });
            });
            return data1.map((item: BodResponse) => {
                return { ...item, ...(data2Map.get(item.id) || {}) };
            });
        } else {
            return data1;
        }
    }
    return [];
};

export const getPercent = (value: number, total: number) => {
    if (value === 0) return 0;
    return Math.ceil((value / total) * 100);
};

export const getPercentColor = (value: number) => {
    if (value > 75) return 'success.main';
    if (value >= 25 && value <= 75) return 'warning.main';
    if (value < 25) return 'error.main';
    return 'success.main';
};

export enum ReuseDialogTypeLabel {
    datasheet = 'Datasheet',
    warranty = 'Warranty',
    installation_guide = 'Installation Guide',
    drawing = 'Drawing',
    other_document = 'Other Document',
    alternates = 'product'
}

export enum ReuseDialogType {
    DATASHEET = 'datasheet',
    WARRANTY = 'warranty',
    GUIDE = 'installation_guide',
    DRAWING = 'drawing',
    OTHER = 'other_document'
}

export const deleteTitle = (isSectionSelected: boolean) => {
    if (isSectionSelected) {
        return 'Are you sure you want to delete this section and all its products?';
    }
    return `Are you sure you want to delete the selected product(s)?`;
};

export const deleteMessage = (mainProductOptionsInfo: Record<number, Array<number>>, deleteIds: number[], isSectionSelected?: boolean) => {
    if (isSectionSelected) return DELETE_SECTION_CONTENT;
    if (isLinkedWithOptions(mainProductOptionsInfo, deleteIds)) {
        if (deleteIds.length > 1) {
            return deleteMultipleWithOptionsText;
        } else {
            return deleteContentWithOption;
        }
    } else {
        return deleteContent;
    }
};

export const DELETE_SECTION_TITLE = 'Delete Section(s)';
export const DELETE_SECTION_CONTENT = 'This will delete the section(s) throughout this BOM, and the change will be reflected on all the other tabs.';
export const areYouSure = 'Are you sure?';
export const deleteDialogTitle = 'Delete Product(s)';
export const deleteContent = 'This will delete the product(s) throughout this BOM, and the change will be reflected on all the other tabs. ';
export const deleteContentWithOption = 'This will delete the product(s) throughout this BOM and break any associations with linked options. This change will be reflected on all the other tabs.';
export const deleteMultipleWithOptionsText =
    'One or more products selected have associated options, and deleting these products will break this relationship. \n\n Are you sure you want to delete the selected product(s)?';

export const downloadFile = (url: string) => {
    const proxy_file = getProxyFileUrl(url);
    const fileName = url.split('/').pop();
    fetch(proxy_file)
        .then((res) => res.blob())
        .then((blob) => {
            const file = new Blob([blob], { type: 'application/pdf' });
            FileSaver.saveAs(file, fileName);
        });
};

export const options = [
    {
        label: 'Add a link to a file',
        value: 'addLink'
    },
    { label: 'Upload a file', value: 'addFile' },
    { label: 'Add a blank page', value: 'addBlankPage' },
    { label: `Reuse from another product in this BOM`, value: 'reuse' }
];
export const addProductManuallyOptions = [
    {
        label: 'Add a link to a file',
        value: 'addLink'
    },
    { label: 'Upload a file', value: 'addFile' }
];

export function getCancelButtonUrl(projectId = '', bomId = '', tabName = '', productId = '') {
    return `/v2/project/${projectId}/bom/${bomId}?tab=${tabName}&productId=${productId}`;
}

export const aggregateDocumentHashAndVersion = (datasheetTabData?: DatasheetTabDataResponse[]) => {
    if (!datasheetTabData) return;

    const documentHashAndVersion: hashAndVersionRequest[] = [];

    for (let i = 0; i < datasheetTabData.length; i++) {
        if (datasheetTabData[i].selected_datasheet_id) {
            const selectedDatasheet = datasheetTabData[i].selected_datasheet as SelectedDatasheetData;
            documentHashAndVersion.push({ hash: selectedDatasheet.document_hash, version: selectedDatasheet.document_hash_version });
        }
    }
    return documentHashAndVersion;
};

export const isStartColBigger = (startCol: string, endCol: string) => {
    if (startCol.length === endCol.length) return startCol > endCol;

    return startCol.length > endCol.length;
};

export function getAmountForUi(amount: number) {
    const intl = new Internationalization();
    const amountForUi = intl.formatNumber(amount, { format: 'N2', useGrouping: true, minimumFractionDigits: 2 });

    return amountForUi;
}
export const isRecognisedFileFormat = (obj: UploadedScheduleResponse | UploadedSubmittalPackageResponse | UploadedScheduleResponse[] | UploadedSubmittalPackageResponse[] | UploadedDoc) => {
    const formats = [
        RECOGNISED_EXCEL_FOMRATS.OASIS_EXCEL,
        RECOGNISED_EXCEL_FOMRATS.EPISOR_EXCEL,
        RECOGNISED_EXCEL_FOMRATS.AGILE_EXCEL,
        RECOGNISED_EXCEL_FOMRATS.NORMAL_EXCEL,
        RECOGNISED_EXCEL_FOMRATS.PARSPEC_EXCEL
    ];
    if ('filetype' in obj && 'file_format' in obj) {
        return obj['filetype'] === SCHEDULE_FILE_TYPES.xmlFileType || formats.includes(String(obj['file_format']));
    }

    return false;
};

export function isLinkedWithOptions(mainProductOptionsInfo: Record<number, Array<number>>, deleteIds: number[]) {
    return deleteIds.find((id) => Object.keys(mainProductOptionsInfo).includes(String(id)));
}

export function getMainProductOptionObj(bomData: Array<BodResponse>) {
    const mainProductAndOptionsMap: Record<number, Array<number>> = {};
    for (const bodData of bomData) {
        if (bodData.product_to_replace && Array.isArray(mainProductAndOptionsMap[bodData.product_to_replace])) {
            mainProductAndOptionsMap[bodData.product_to_replace].push(bodData.id);
        } else if (bodData.product_to_replace) {
            mainProductAndOptionsMap[bodData.product_to_replace] = [bodData.id];
        }
    }

    return mainProductAndOptionsMap;
}

export function getUsdFormattedString(amount: number) {
    const usDollarFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    return usDollarFormatter.format(amount);
}

export function getPercentFormattedString(percent: number, defaultPrecision: number = 2) {
    const percentFormatter = new Intl.NumberFormat('default', {
        style: 'percent',
        minimumFractionDigits: 2,
        maximumFractionDigits: defaultPrecision
    });

    return percentFormatter.format(percent / 100);
}

export function getCellAmountFormatter(params: ValueFormatterParams<any, any>) {
    if (params?.value == null || params?.value == 0) {
        return '';
    }
    return getUsdFormattedString(params.value);
}

export function getCellPercentFormatter(params: ValueFormatterParams<any, any>) {
    if (params?.value == null) {
        return '';
    }

    return getPercentFormattedString(params.value);
}

export const handleProcessCellPasteForQty = (args: ProcessCellForExportParams) => {
    const { column, value } = args;
    const fieldName = column.getColId();
    if (fieldName === 'qty') {
        if (value !== null && value !== '' && !isNaN(value as unknown as number)) {
            return Number(value);
        } else {
            return null;
        }
    }
    return value;
};

export const pricingCellParams = {
    precision: 2,
    preventStepping: true,
    min: Number.MIN_SAFE_INTEGER,
    max: Number.MAX_SAFE_INTEGER
};

export const TextCellParams = {
    maxLength: 255
};
export const getRowId = (params: GetRowIdParams) => {
    return params?.data?.id;
};

export function getTableHeight({
    rowHeight,
    rows,
    tableMaxHeight,
    hasScroll = true,
    hasFooter = false,
    hasOption = false,
    isOptionTable = false
}: {
    rowHeight: number;
    rows: number;
    tableMaxHeight: number;
    hasScroll?: boolean;
    hasFooter?: boolean;
    hasOption?: boolean;
    isOptionTable?: boolean;
}) {
    // table rows height + header + footer
    if (!rows) return 317;
    let currentHeightOfTable = rowHeight * (rows || 1) + (hasScroll ? 47 : 35);
    if (hasFooter) {
        currentHeightOfTable -= 36;
    }
    if (hasOption && currentHeightOfTable > tableMaxHeight - 95) {
        currentHeightOfTable -= 31;
    }
    return currentHeightOfTable > tableMaxHeight - 95 ? (hasOption ? tableMaxHeight - 91 : tableMaxHeight - (isOptionTable ? 91 : 30)) : currentHeightOfTable;
}

export const calculateKitCost = (tableData: MainProductDataType[], kitQuantity: number) => {
    const pricingData = {
        cost: 0,
        discountedCost: 0,
        sellPrice: 0,
        discount: 0,
        margin: 0,
        extendedPrice: 0,
        extendedDiscountCost: 0,
        per: 1
    };
    const isAllNull = tableData?.every((item) => (item.cost === null && item.sellPrice === null) || item.qty === null || item.is_hidden);
    if (isAllNull) return { cost: null, discountedCost: null, discount: null, margin: null, sellPrice: null, extendedPrice: null, extendedDiscountCost: null, per: 1 };
    tableData.forEach((item: MainProductDataType) => {
        if (!item.is_hidden) {
            const { cost = 0, discountedCost = 0, sellPrice = 0, qty = 0, per = 1 } = item;
            const quantity = (qty || 0) as any;
            const validPer = per || 1;
            pricingData.cost += ((cost || 0) * quantity.toFixed(2)) / validPer;
            pricingData.discountedCost += ((discountedCost || 0) * quantity.toFixed(2)) / validPer;
            pricingData.sellPrice += ((sellPrice || 0) * quantity.toFixed(2)) / validPer;
            pricingData.extendedDiscountCost += Number(((discountedCost || 0 * quantity) / validPer).toFixed(2));
            pricingData.extendedPrice += Number(((sellPrice || 0 * quantity) / validPer).toFixed(2));
        }
    });
    if (!pricingData.cost) {
        return { ...pricingData, discountedCost: null, discount: null, margin: null, extendedDiscountCost: null, extendedPrice: null };
    }
    pricingData.discount = pricingData.cost === 0 ? 0 : Number(((1 - pricingData.discountedCost / pricingData.cost) * 100).toFixed(2));
    pricingData.margin = pricingData.sellPrice === 0 || pricingData.discountedCost === 0 ? 0 : Number(((1 - pricingData.discountedCost / pricingData.sellPrice) * 100).toFixed(2));
    pricingData.extendedPrice = (pricingData.sellPrice * kitQuantity) / pricingData.per;
    return pricingData;
};

export const kitToolbarItems = ['add', 'delete', 'hide', 'duplicate'] as ToolBarT[];
export const DRAGABLE_TYPES = {
    SECTION: 'Section'
};

export const getAdjacentColumnNames = (tableRef: React.RefObject<AgGridReact>, startColumnName: string, filterColums?: string[]) => {
    const gridApi = tableRef.current?.api;

    if (!gridApi) {
        return [];
    }

    let columns: ColumnInfo[] = gridApi.getAllDisplayedColumns().map((col: Column) => ({
        field: col.getColId(),
        cellDataType: typeof col.getColDef()?.cellDataType === 'string' ? (col.getColDef()?.cellDataType as string) : null
    }));

    if (columns) {
        const sliceIndex = columns.findIndex((item) => item.field === startColumnName);
        if (sliceIndex !== -1) {
            columns = columns.slice(sliceIndex);
        }

        if (filterColums?.length) {
            columns = columns.filter((col) => col?.field && !filterColums.includes(col.field));
        }
    }
    return columns;
};

function checkProductType(node: IRowNode): 'section' | 'product' | 'component' {
    if (node.level === ROW_LEVEL.SECTION) {
        return 'section';
    } else if (node.level === ROW_LEVEL.PRODUCT) {
        return 'product';
    }
    return 'component';
}

export const getParsedContentFromClipboard = async () => {
    const text = await navigator.clipboard.readText();
    if (text.length) {
        return text
            .trim()
            .split('\n')
            .map((row) => row.split('\t').map((cell) => cell.replace(/\r/g, '')));
    }
    return [];
};

const getLastUpdatedRowId = (selectedRowsRef: MutableRefObject<Set<number>>) => {
    return Array.from(selectedRowsRef.current)[selectedRowsRef.current.size - 1];
};

export const addNewRowsToTheTable = async (
    selectedRowsRef: MutableRefObject<Set<number>>,
    tableRef: React.RefObject<AgGridReact>,
    startColumnName: string,
    createNewRowsFromClipboard: (
        lastUpdatedBodId: number,
        selectedColumns: Set<{
            field?: string;
            cellDataType: string | null;
        }>,
        newRowsData?: string[][]
    ) => Promise<void>,
    toRemoveColumnList?: Array<string>
) => {
    try {
        const rowsData = await getParsedContentFromClipboard();
        if (selectedRowsRef.current.size && tableRef.current && rowsData.length) {
            const columnsData = new Set(getAdjacentColumnNames(tableRef, startColumnName, ['id', 'menu', 'drag', ...(toRemoveColumnList || [])]));
            const lastUpdatedBodId = getLastUpdatedRowId(selectedRowsRef);
            const newRowsData = rowsData.slice(selectedRowsRef.current.size);
            createNewRowsFromClipboard(lastUpdatedBodId, columnsData, newRowsData);
            selectedRowsRef.current.clear();
        }
    } catch (error) {
        console.error(`Error while adding new rows to the table: `, error);
    }
};

export const handleOnRangeSelectionChanged = (
    event: RangeSelectionChangedEvent,
    tableRef: React.RefObject<AgGridReact>,
    selectedRowsRef: MutableRefObject<Set<number>>,
    currentGroupWhilePastingRef?: MutableRefObject<number | null>
) => {
    const cellRanges = tableRef.current?.api.getCellRanges();
    if (!cellRanges || cellRanges.length === 0) {
        return;
    }
    selectedRowsRef.current.clear();
    let startRow = 0,
        endRow = 0;
    if (cellRanges) {
        cellRanges.forEach((range: CellRange) => {
            startRow = Math.min(Number(range.startRow?.rowIndex), Number(range.endRow?.rowIndex));
            endRow = Math.max(Number(range.startRow?.rowIndex), Number(range.endRow?.rowIndex));
        });
    }

    if (event.finished) {
        let startRowNode: IRowNode | undefined;
        let endRowNode: IRowNode | undefined;

        if (endRow !== startRow) {
            startRowNode = tableRef.current?.api.getDisplayedRowAtIndex(startRow);
        }
        endRowNode = tableRef.current?.api.getDisplayedRowAtIndex(endRow);
        let currentValidRows: Array<number> | undefined = [];
        const firstNode = startRowNode ? startRowNode : endRowNode;

        if (currentGroupWhilePastingRef) {
            currentGroupWhilePastingRef.current = firstNode?.data?.section || firstNode?.data?.substitute_section;
        }

        if (firstNode) {
            const productType = checkProductType(firstNode);
            let groupId: number | null = firstNode.data.groupPath[0];

            const getValidRows = (groupLength: number, minRowIndex: number) => {
                const displayedRowCount = tableRef.current?.api?.getDisplayedRowCount(); // Get the count of displayed rows
                const validRows = [];
                if (displayedRowCount) {
                    for (let i = 0; i < displayedRowCount; i++) {
                        const rowNode = tableRef.current?.api?.getDisplayedRowAtIndex(i); // Get each displayed row
                        if (rowNode) {
                            const { rowIndex, data } = rowNode;
                            // Check if row meets the conditions
                            if (rowIndex && rowIndex >= minRowIndex && data.groupPath.length === groupLength && data.groupPath.includes(groupId)) {
                                validRows.push(Number(data?.id));
                            }
                        }
                    }
                }

                return validRows;
            };

            switch (productType) {
                case 'section':
                    currentValidRows = getValidRows(2, Number(firstNode.rowIndex) + 1);
                    break;

                case 'product':
                    currentValidRows = getValidRows(2, Number(firstNode.rowIndex));
                    break;

                case 'component':
                    groupId = firstNode.data.groupPath[1];
                    currentValidRows = getValidRows(3, Number(firstNode.rowIndex));
                    break;

                default:
                    break;
            }
        }

        if (currentValidRows?.length) {
            selectedRowsRef.current = new Set(currentValidRows);
        }
    }
};

function hasDefaultValuesWarningValue(item: MainProductComponentsDataType, field: string) {
    if (checkIfInDiscountFields(field)) {
        return field in DEFAULT_VALUES_WARNING && item[field as keyof MainProductComponentsDataType] !== null && item[field as keyof MainProductComponentsDataType] !== item['cost'];
    }
    return (
        !checkIfInDiscountFields(field) &&
        field in DEFAULT_VALUES_WARNING &&
        item?.[field as keyof MainProductComponentsDataType] &&
        item?.[field as keyof MainProductComponentsDataType] !== DEFAULT_VALUES_WARNING[field as keyof DefaultValues]
    );
}

export function checkDefaultValuesWarningCount(data: MainProductComponentsDataType[], fields: string[]) {
    let found: { [key: string]: boolean } = {};
    for (const item of data) {
        for (const field of fields) {
            if (!(field in found) && hasDefaultValuesWarningValue(item, field)) {
                found[field] = true;
            }
            // Check components if they exist
            if (item.components && item.components.length > 0) {
                for (const component of item.components) {
                    if (hasDefaultValuesWarningValue(component, field)) {
                        found[field] = true;
                    }
                }
            }
        }
    }
    return Object.keys(found).length;
}

const RANGE_DELIMETER = '-';
// maximum 9 digit number
const MAX_LEAD_TIME_VALUE = 999_999_999;

const removeBlankSpaces = (item: string) => item.replace(/\s+/g, '');
const getIsRange = (value: string) => value.includes(RANGE_DELIMETER);
const convertToRange = (value: string) => `${value}${RANGE_DELIMETER}`;
const getMinMax = (value: string) => value.split(RANGE_DELIMETER);
const isStrictlyNumeric = (item: string) => /^\d+$/.test(item);
const isValueOutOfRange = (value: number) => value > MAX_LEAD_TIME_VALUE;

const INVALID_RANGE = 'Lead Time Value format must be # or #-#, and not exceed 9 digits.';
const VALUE_ADJUSTED = 'Lead Time Value adjusted to match cell format.';

type ValidatorResponseType =
    | {
          isError: true;
          message: string;
      }
    | {
          isError: false;
          tuple: Array<number | null>;
          message?: string;
      };

interface LeadTimeRange {
    leadTimeMin?: null | number;
    leadTimeMax?: null | number;
}

export function getFormattedLeadTimeRange({ leadTimeMin, leadTimeMax }: LeadTimeRange) {
    if (isNil(leadTimeMin)) return '';
    if (isNil(leadTimeMax)) return `${leadTimeMin}`;
    return `${leadTimeMin}-${leadTimeMax}`;
}

//   We adjust value when we remove extra dash, correct order, remove spaces
export function getValidatedLeadTimeRange(rawNewValue: string): ValidatorResponseType {
    // in case user is trying to clear the value
    if (!rawNewValue) {
        return { isError: false, tuple: [null, null] };
    }

    const newValue = removeBlankSpaces(rawNewValue);
    // Invalid range: As user only added multiple whitespaces
    if (!newValue) {
        return { isError: true, message: INVALID_RANGE };
    }

    const isRange = getIsRange(newValue);
    const rangeArr = getMinMax(isRange ? newValue : convertToRange(newValue));

    if (rangeArr.length !== 2) {
        // Invalid range: there should only be two items min and max(they can be empty)
        return { isError: true, message: INVALID_RANGE };
    }

    // remove empty items, this could happen if user entered only one bound or no bounds
    // (Eg: - or # or #- or -#)
    const rangeItems = rangeArr.filter(Boolean);
    if (isEmpty(rangeItems) || !rangeItems.every(isStrictlyNumeric)) {
        return { isError: true, message: INVALID_RANGE };
    }

    const tuple = rangeItems.map((item) => parseInt(item, 10));

    if (tuple.some(isValueOutOfRange)) {
        return { isError: true, message: INVALID_RANGE };
    }

    const [min, max = null] = tuple;

    // if max bound is present, check and correct the order
    if (!isNil(max) && max <= min) {
        return {
            isError: false,
            message: VALUE_ADJUSTED,
            tuple: min === max ? [min, null] : [max, min]
        };
    }

    if (isRange && isNil(max)) {
        // If user entered incomplete range eg: #- or -#, we adjust it to #, so notify user
        return {
            isError: false,
            message: VALUE_ADJUSTED,
            tuple: [min, max]
        };
    }

    return { isError: false, tuple: [min, max] };
}

export const GET_AUTO_SEARCH_OPTIONS = (onClick: (docType: string, type: 'me' | 'company_users' | 'company_group_users') => void) => [
    {
        label: 'All Documents',
        items: [
            { label: 'My History', callback: () => onClick('alldocs', 'me') },
            { label: 'Location History', callback: () => onClick('alldocs', 'company_users') },
            { label: 'Team History', callback: () => onClick('alldocs', 'company_group_users') }
        ]
    },
    {
        label: 'Datasheets',
        items: [
            { label: 'My History', callback: () => onClick('datasheet', 'me') },
            { label: 'Location History', callback: () => onClick('datasheet', 'company_users') },
            { label: 'Team History', callback: () => onClick('datasheet', 'company_group_users') }
        ]
    },
    {
        label: 'Install Guides',
        items: [
            { label: 'My History', callback: () => onClick('installGuide', 'me') },
            { label: 'Location History', callback: () => onClick('installGuide', 'company_users') },
            { label: 'Team History', callback: () => onClick('installGuide', 'company_group_users') }
        ]
    },
    {
        label: 'Warranties',
        items: [
            { label: 'My History', callback: () => onClick('warranty', 'me') },
            { label: 'Location History', callback: () => onClick('warranty', 'company_users') },
            { label: 'Team History', callback: () => onClick('warranty', 'company_group_users') }
        ]
    }
];

export const getIsLastNodeInSectionHierarchy = (node: IRowNode) => {
    if (node.lastChild && node.level > ROW_LEVEL.SECTION) {
        const isProductNode = node.level === ROW_LEVEL.PRODUCT;
        const isComponentNode = node.level === ROW_LEVEL.COMPONENT;
        if (isProductNode && node.allChildrenCount && node.expanded) {
            // though this is last product row, but it is expanded and children are visible
            // so let user navigate to it with Enter, and do not add new line
            return false;
        }

        // This is component node and it is last of it's parent product,
        // but the parent product itself is not last in the section
        if (isComponentNode && !node.parent?.lastChild) {
            return false;
        }
        return true;
    }

    return false;
};

export const suppresskeyboardEventForEnter = (params: SuppressKeyboardEventParams) => {
    const isLastNode = getIsLastNodeInSectionHierarchy(params.node);
    if (isLastNode) {
        return true;
    }
    return false;
};

export const calculateTableHeight = (displayedCount: number) => {
    const rowHeight = 36;
    return displayedCount * rowHeight + 48;
};

export function formatSelectedItemsText(rowNode: IRowNode | undefined): string {
    const isChildrenAvailable = rowNode?.allChildrenCount !== null;

    if (rowNode?.level === 0) {
        return `1 Section(s)`;
    } else if (rowNode?.level === 1 && !isChildrenAvailable) {
        return `1 Product(s)`;
    } else if (rowNode?.level === 1 && isChildrenAvailable) {
        return `1 Kit(s)`;
    } else if (rowNode?.level === 2) {
        return `1 Component(s)`;
    }

    return '';
}

export const isTabSupportImportProducts = (value: string): value is TabSupportImportProducts => {
    return TAB_SUPPORTING_IMPORT_PRODUCTS.includes(value as TabSupportImportProducts);
};

export const getBomTabFromLocationAfterProductImport = (location: Location): string => {
    const locationBomTab = location.state?.bomTab as unknown as string | undefined | null;
    const bomTab = isTabSupportImportProducts(String(locationBomTab)) ? locationBomTab : 'specification';
    return bomTab || 'specification';
};

export function getLotServicesTableHeight({ rowHeight, rows, tableMaxHeight, isServicesTable }: { rowHeight: number; rows: number; tableMaxHeight: number; isServicesTable?: boolean }) {
    if (!rows) return 150;
    let currentHeightOfTable = rowHeight * (rows || 1) + (isServicesTable ? 76 : 88);

    return currentHeightOfTable > tableMaxHeight - 65 ? tableMaxHeight - 50 : currentHeightOfTable;
}

export const applyRounding = (num: number, roundingBehaviour?: RoundingBehaviour): number => {
    const precisionMap: { [key in Exclude<RoundingBehaviour, '' | undefined>]: number } = {
        '0.05': 0.05,
        '0.10': 0.1,
        '0.25': 0.25,
        '0.50': 0.5,
        '1.00': 1.0
    };

    if (roundingBehaviour) {
        const precision = precisionMap[roundingBehaviour];
        if (precision !== undefined) {
            num = Math.ceil(num / precision) * precision;
        }
    }

    return Number(num.toFixed(2));
};

export const getLotsTotalPrices = (lotsData?: LotsResponse[], rounding?: RoundingBehaviour, roundingFieldName?: string) => {
    if (!lotsData) {
        return;
    }
    let lotsTotalExtPrice = 0;
    let lotsExtDistCost = 0;
    let lotsTotalCost = 0;
    for (const lotData of lotsData) {
        const sellPrice = convertToDollar(lotData.sell_price_cents || 0);
        lotsTotalExtPrice += roundingFieldName === NO_ROUNDING ? sellPrice : Number(applyRounding(sellPrice, rounding));
        lotsTotalCost += convertToDollar(lotData.cost_cents || 0);
        lotsExtDistCost += convertToDollar(lotData.discounted_cost_cents || 0);
    }

    return { lotsTotalExtPrice, lotsExtDistCost, lotsTotalCost };
};

export const getLotsObjForMainProductTable = (lotsData: LotsResponse[]) => {
    const lotsObj = lotsData.reduce((currLotsObj, lotData) => {
        const lotsInfo = {
            name: lotData.name,
            extendedPrice: convertToDollar(lotData.sell_price_cents || 0),
            extendedCost: convertToDollar(lotData.cost_cents || 0),
            extendedDiscountCost: convertToDollar(lotData.discounted_cost_cents || 0),
            isUsedInOtherSection: false
        };
        currLotsObj[lotData.id] = { lotsInfo, lotsUsedInSectionsSet: new Set<number>() };
        return currLotsObj;
    }, {} as { [id: number]: { lotsInfo: LotInfoType; lotsUsedInSectionsSet: Set<number> } });

    return lotsObj;
};

export const prepareAllBomTableDataFromApis = ({
    initBodsData,
    pricingData,
    lotsData,
    roundingPrecision,
    roundingBehaviourOnCell,
    submittalData,
    omData,
    mainProductOptionsInfo
}: PricingTableFromApis) => {
    if (!initBodsData || !pricingData || !lotsData) {
        return {};
    }

    const bodsData = !Array.isArray(initBodsData) ? [] : [...initBodsData];
    const lotsObj = getLotsObjForMainProductTable(lotsData);

    // If we are on documents tab, then only we will have submittals and oms data map
    const submittalsMap = submittalData?.length ? new Map(submittalData.map((item) => [item.bod_id, item])) : null;
    const omsMap = omData?.length ? new Map(omData.map((item) => [item.bod_id, item])) : null;

    const pricingDataMap = pricingData.reduce((acc: Record<number, PricingBodsResponse[0]>, pricingBodData) => {
        acc[pricingBodData.bod_id] = pricingBodData;
        return acc;
    }, {});

    const bodsDataMap = bodsData.reduce((acc: Record<number, BodResponse>, bodData) => {
        acc[bodData.id] = bodData;
        return acc;
    }, {});

    let bodsExtendedPrice = 0,
        bodsCost = 0,
        bodsExtDiscountedPrice = 0,
        bodsExtCost = 0;

    // to handle backend sending response as {} when no bods are present
    const tableData = bodsData.flatMap((bodData) => {
        const bodId = bodData.id;
        // to ensure that tableData has both bod and pricing data fetched
        if (!pricingDataMap[bodId]) {
            return [];
        }
        // logic to add isUsedInOtherSection flag in lotsInfo obj
        const isMainProduct = Boolean(bodData.section);
        const sectionId = bodData.section || 0;
        const optionSectionId = bodData.substitute_section || 0;
        const pricingDataForBod = pricingDataMap[bodId] || {
            notes: null,
            lot_id: null,
            cost_cents: null,
            discount_percentage: null,
            discounted_cost_cents: null,
            extended_discounted_cost_cents: null,
            extended_cost_cents: null,
            margin_percentage: null,
            sell_price_cents: null,
            lead_time_unit: null,
            lead_time_min: null,
            lead_time_max: null,
            source_quote_number: null,
            per: 1
        };
        // calculating individual bod price for current row & total sum of prices.
        const extendedSellPrice =
            (pricingDataForBod.sell_price_cents || pricingDataForBod.sell_price_cents === 0) && (bodData.qty || bodData.qty === 0)
                ? (convertToDollar(pricingDataForBod.sell_price_cents) * bodData.qty) / pricingDataForBod.per
                : null;
        const extendedCost = pricingDataForBod.cost_cents && bodData.qty ? (convertToDollar(pricingDataForBod.cost_cents) * bodData.qty) / pricingDataForBod.per : null;
        const discountedCost = pricingDataForBod.discounted_cost_cents && convertToDollar(pricingDataForBod.discounted_cost_cents);
        const cost = pricingDataForBod.cost_cents && convertToDollar(pricingDataForBod.cost_cents);
        const sellPrice = pricingDataForBod.sell_price_cents && convertToDollar(pricingDataForBod.sell_price_cents);
        const extendedDiscountCost =
            (pricingDataForBod.discounted_cost_cents || pricingDataForBod.discounted_cost_cents === 0) && (bodData.qty || bodData.qty === 0)
                ? (convertToDollar(pricingDataForBod.discounted_cost_cents) * bodData.qty) / pricingDataForBod.per
                : null;
        const lotId = pricingDataForBod?.lot_id || null;
        const {
            lotsInfo,
            lotsUsedInSectionsSet
        }: {
            lotsInfo?: LotInfoType;
            lotsUsedInSectionsSet?: Set<number>;
        } = lotId && lotsObj[lotId] ? lotsObj[lotId] : {};

        if (lotId && lotsObj[lotId] && lotsUsedInSectionsSet && lotsInfo && !bodData.is_hidden) {
            lotsUsedInSectionsSet.add(sectionId);
            if (lotsUsedInSectionsSet?.size > 1 && !lotsInfo.isUsedInOtherSection) {
                lotsInfo.isUsedInOtherSection = true;
            }
        }

        if (isMainProduct && !bodData.kit && !lotId && lotId !== 0 && !bodData.is_hidden && bodData.qty && extendedSellPrice !== null) {
            bodsExtendedPrice += extendedSellPrice || 0;
            bodsExtDiscountedPrice += ((discountedCost || 0) * bodData.qty) / pricingDataForBod.per;
            bodsCost += ((cost || 0) * bodData.qty) / pricingDataForBod.per;
            bodsExtCost += extendedCost || 0;
        }

        let productToReplaceInfo, addDeductAmount;
        if (!isMainProduct && bodData.product_to_replace && pricingDataMap[bodData.product_to_replace]) {
            const mainProductPricingInfo = pricingDataMap[bodData.product_to_replace];
            const mainProductInfo = bodsDataMap[bodData.product_to_replace];
            const mainProductExtPrice =
                (mainProductPricingInfo.sell_price_cents || mainProductPricingInfo.sell_price_cents === 0) && (mainProductInfo.qty || mainProductInfo.qty === 0)
                    ? (convertToDollar(mainProductPricingInfo.sell_price_cents) * mainProductInfo.qty) / mainProductPricingInfo.per
                    : null;
            productToReplaceInfo = {
                [BOM_TABLES_ENUM.EXTENDED_PRICE]: mainProductExtPrice,
                [BOM_TABLES_ENUM.PRODUCT_TO_REPLACE_LABEL]: [mainProductInfo?.category?.toUpperCase(), mainProductInfo?.manufacturer?.toUpperCase(), mainProductInfo?.model_number?.toUpperCase()]
                    .filter((data) => data)
                    .join(' | ')
            };
        }

        if (!isMainProduct) {
            const mainProductExtPrice = productToReplaceInfo?.[BOM_TABLES_ENUM.EXTENDED_PRICE];
            addDeductAmount =
                (mainProductExtPrice || mainProductExtPrice === 0) && (extendedSellPrice || extendedSellPrice === 0) && bodData.product_to_replace
                    ? extendedSellPrice - mainProductExtPrice
                    : extendedSellPrice;
        }
        const currentBodHasOptions = isMainProduct && mainProductOptionsInfo[bodData.id]?.length > 0;

        return {
            [BOM_TABLES_ENUM.ID]: bodId,
            [BOM_TABLES_ENUM.QTY]: bodData.qty,
            [BOM_TABLES_ENUM.TYPE]: bodData.category,
            [BOM_TABLES_ENUM.MANUFACTURER]: bodData.manufacturer,
            [BOM_TABLES_ENUM.MODEL_NO]: bodData.model_number,
            [BOM_TABLES_ENUM.DESCRIPTION]: bodData.description,
            [BOM_TABLES_ENUM.SPECIFICATION]: bodData.specification,
            [BOM_TABLES_ENUM.QUOTE_NOTES]: pricingDataForBod.notes,
            [BOM_TABLES_ENUM.LOT]: lotId,
            [BOM_TABLES_ENUM.COST]: cost,
            [BOM_TABLES_ENUM.DISCOUNT]: pricingDataForBod.discount_percentage,
            [BOM_TABLES_ENUM.DISCOUNT_COST]: discountedCost,
            [BOM_TABLES_ENUM.EXT_DISCOUNT_COST]: extendedDiscountCost,
            [BOM_TABLES_ENUM.MARGIN]: pricingDataForBod.margin_percentage,
            [BOM_TABLES_ENUM.SELL_PRICE]: sellPrice,
            [BOM_TABLES_ENUM.EXTENDED_PRICE]: extendedSellPrice,
            [BOM_TABLES_ENUM.EXT_COST]: extendedCost,
            [BOM_TABLES_ENUM.LEAD_TIME_UNIT]: pricingDataForBod.lead_time_unit,
            [BOM_TABLES_ENUM.LEAD_TIME_MIN]: pricingDataForBod.lead_time_min !== -1 ? pricingDataForBod.lead_time_min : null,
            [BOM_TABLES_ENUM.LEAD_TIME_MAX]: pricingDataForBod.lead_time_max !== -1 ? pricingDataForBod.lead_time_max : null,
            [BOM_TABLES_ENUM.QUOTE_NO]: pricingDataForBod.source_quote_number,
            [BOM_TABLES_ENUM.LOT_INFO]: lotsInfo || null,
            [BOM_TABLES_ENUM.IS_HIDDEN]: bodData.is_hidden,
            [BOM_TABLES_ENUM.SECTION]: sectionId,
            [BOM_TABLES_ENUM.PRODUCT_TO_REPLACE]: bodData.product_to_replace,
            [BOM_TABLES_ENUM.PRODUCT_TO_REPLACE_INFO]: productToReplaceInfo,
            [BOM_TABLES_ENUM.ADD_DEDUCT_AMT]: addDeductAmount,
            [BOM_TABLES_ENUM.INTERNAL_COMMENTS]: pricingDataForBod.internal_notes,
            [BOM_TABLES_ENUM.OPTION_SECTION]: optionSectionId,
            [BOM_TABLES_ENUM.HAS_OPTION]: currentBodHasOptions,
            [BOM_TABLES_ENUM.KIT]: bodData.kit,
            [BOM_TABLES_ENUM.IS_KIT]: bodData.is_kit,
            [BOM_TABLES_ENUM.ORDER]: bodData.order,
            [BOM_TABLES_ENUM.IS_HIGHLIGHTED]: bodData.is_highlighted,
            [BOM_TABLES_ENUM.PER]: pricingDataForBod.per,
            [BOM_TABLES_ENUM.NOTES]: submittalsMap ? submittalsMap.get(bodId)?.notes : '',
            [BOM_TABLES_ENUM.DATASHEET]: submittalsMap ? submittalsMap.get(bodId)?.selected_datasheet : null,
            [BOM_TABLES_ENUM.SELECTED_DATASHEET_ID]: submittalsMap ? submittalsMap.get(bodId)?.selected_datasheet_id : null,
            [BOM_TABLES_ENUM.INSTALLATION_GUIDE]: omsMap ? omsMap.get(bodId)?.selected_installation_guide : null,
            [BOM_TABLES_ENUM.DRAWING]: omsMap ? omsMap.get(bodId)?.selected_drawing : null,
            [BOM_TABLES_ENUM.WARRANTY]: omsMap ? omsMap.get(bodId)?.selected_warranty : null,
            [BOM_TABLES_ENUM.OTHER_DOC]: omsMap ? omsMap.get(bodId)?.selected_other_document : null,
            [BOM_TABLES_ENUM.ALL_PAGES_HIDDEN]: submittalsMap ? submittalsMap.get(bodId)?.all_pages_hidden : false
        };
    });

    const { lotsTotalExtPrice = 0, lotsExtDistCost = 0, lotsTotalCost = 0 } = getLotsTotalPrices(lotsData, roundingPrecision, roundingBehaviourOnCell) || {};
    const totalExtendedPrice = lotsTotalExtPrice + bodsExtendedPrice;
    const totalExtDiscountedCost = lotsExtDistCost + bodsExtDiscountedPrice;
    const totalCost = bodsCost + lotsTotalCost;
    const totalExtCost = bodsExtCost + lotsTotalCost;
    const totalMarginCost = totalExtendedPrice - totalExtDiscountedCost;
    const totalMarginPercentage = totalExtDiscountedCost && totalExtendedPrice ? (1 - totalExtDiscountedCost / totalExtendedPrice) * 100 : 0;
    const totalDiscount = totalCost - totalExtDiscountedCost;
    const totalDiscountPercentage = totalExtDiscountedCost && totalCost ? (1 - totalExtDiscountedCost / totalCost) * 100 : 0;
    return {
        tableData,
        totalExtendedPrice,
        totalCost,
        totalExtDiscountedCost,
        totalExtCost,
        totalMarginCost,
        totalMarginPercentage,
        totalDiscount,
        totalDiscountPercentage
    };
};

export function setPotentialParentRowDragAndDropNode(
    api: GridApi,
    overNode: IRowNode | null | undefined,
    potentialParent: MutableRefObject<IRowNode | null>,
    currentDropNode?: MutableRefObject<CurrentDropNodeType | null>,
    mousePosition?: number
) {
    const newPotentialParent = overNode ? (overNode.level === 0 || (overNode.level === 1 && overNode.allChildrenCount) || overNode.data.is_kit ? overNode : overNode.parent) : null;
    const newCurrentDropNode = overNode ? overNode : null;

    if (potentialParent?.current === newPotentialParent && currentDropNode?.current === newCurrentDropNode) return;

    const rowsToRefresh = [];
    if (potentialParent.current) rowsToRefresh.push(potentialParent.current);
    if (newPotentialParent) rowsToRefresh.push(newPotentialParent);
    if (currentDropNode?.current) rowsToRefresh.push(currentDropNode.current);
    if (newCurrentDropNode) rowsToRefresh.push(newCurrentDropNode);

    potentialParent.current = newPotentialParent;

    if (currentDropNode) {
        currentDropNode.current = newCurrentDropNode ? { ...newCurrentDropNode, yPosition: mousePosition } : null;
    }
    refreshRows(api, rowsToRefresh);
}

function refreshRows(api: GridApi, rowsToRefresh: IRowNode[]) {
    if (rowsToRefresh.length) {
        api.refreshCells({ rowNodes: rowsToRefresh });
    }
}

export const isHighlightDropLine = (params: CellClassParams | RowDragEndEvent, currentDropNode: CurrentDropNodeType | null, isBottomLine: boolean) => {
    if (!currentDropNode || currentDropNode.yPosition === undefined) return false;

    const yPosition = currentDropNode.yPosition;
    const rowIndex = params.node.rowIndex;
    const currentRowIndex = currentDropNode.rowIndex;

    if (isBottomLine) {
        return rowIndex === currentRowIndex && yPosition > ROW_HEIGHT / 2;
    } else {
        return rowIndex === currentRowIndex && yPosition <= ROW_HEIGHT / 2;
    }
};

export const getSelectedSectionIds = (hierarchy: Record<number, HierachyType>): number[] => {
    const selectedIds: number[] = [];
    for (const [sectionId, sectionData] of Object.entries(hierarchy || {})) {
        if (sectionData && 'isSelected' in sectionData && sectionData.isSelected === true) {
            selectedIds.push(Number(sectionId));
        }
    }
    return selectedIds;
};

export const handleRightClickContextMenuChange = (props: CellContextMenuEvent) => {
    const { event, node, column, value, api } = props;
    if (!event) return;

    const gridApi = api as any;
    const mouseEvent = event as MouseEvent;

    if (gridApi.contextMenuFactory.activeMenu) {
        gridApi.contextMenuFactory.hideActiveMenu();

        const syntheticEvent = {
            clientX: mouseEvent.clientX,
            clientY: mouseEvent.clientY,
            pageX: mouseEvent.pageX,
            pageY: mouseEvent.pageY,
            screenX: mouseEvent.screenX,
            screenY: mouseEvent.screenY,
            target: mouseEvent.target instanceof Element ? mouseEvent.target : (mouseEvent.target as any)?.element,
            type: 'contextmenu'
        };

        gridApi.contextMenuFactory.showMenu(node, column, value, syntheticEvent);
    }
};

export const clearCheckBoxSelectionFromAPI = (params: CellContextMenuEvent) => {
    const selectedRows = params.api.getSelectedRows();
    if (selectedRows.length > 0) {
        params.api.forEachNode((node: IRowNode) => {
            node.setSelected(false);
        });
    }
};
// Utility function to find and format manufacturer name

interface CompanyMfgDetails {
    data: CompanyMfgDetailsResponse[];
}

export const getFormattedManufacturerName = (companyMfgData: CompanyMfgDetails | undefined, manufacturerName: string): string => {
    const matchingManufacturer =
        companyMfgData?.data.find((item: any) => item.manufacturer_name?.toUpperCase() === manufacturerName?.toUpperCase()) ||
        companyMfgData?.data.find((item: any) => item.abbreviations?.includes(manufacturerName?.toUpperCase()));

    return matchingManufacturer?.manufacturer_name?.toUpperCase() || manufacturerName?.toUpperCase();
};
